import { useEffect, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Input, Button, Alert } from '../components'
import { getInviteStatus, submitClientInvite, SubmitClientInvitePayload, InviteStatus } from '../services/inviteService'

interface ClientIntakePageProps {
  inviteId: string
}

interface FormState {
  firstName: string
  lastName: string
  emailAddress: string
  mobileNumber: string
  companyName: string
  streetAddress: string
  suburb: string
  state: string
  postcode: string
  country: string
  bankAccountName: string
  bankAccountBsb: string
  bankAccountNumber: string
  matterPropertyAddress: string
  matterSettlementDate: string
}

const firstNames = [
  'Olivia', 'Liam', 'Emma', 'Noah', 'Ava', 'William', 'Sophia', 'James',
  'Isabella', 'Benjamin', 'Mia', 'Lucas', 'Charlotte', 'Henry', 'Amelia',
  'Alexander', 'Harper', 'Michael', 'Evelyn', 'Ethan',
]

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Wilson', 'Anderson', 'Taylor', 'Thomas',
  'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
]

const streetNames = ['High St', 'Chapel St', 'Church St', 'Station Rd', 'Victoria Ave', 'Beach Rd', 'Park St', 'King St']
const suburbs = ['Melbourne', 'Richmond', 'St Kilda', 'Fitzroy', 'Brunswick', 'South Yarra', 'Carlton', 'Prahran']

const randomFrom = (values: string[]) => values[Math.floor(Math.random() * values.length)]

const randomPropertyAddress = () => `${Math.floor(Math.random() * 200) + 1} ${randomFrom(streetNames)}, ${randomFrom(suburbs)} VIC`

const defaultSettlementDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + 60)
  return d.toISOString().slice(0, 10)
}

const createInitialForm = (): FormState => {
  const firstName = randomFrom(firstNames)
  const lastName = randomFrom(lastNames)
  return {
    firstName,
    lastName,
    emailAddress: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    mobileNumber: '0400000000',
    companyName: '',
    streetAddress: '1 Example Street',
    suburb: 'Melbourne',
    state: 'VIC',
    postcode: '3000',
    country: 'AU',
    bankAccountName: `${firstName} ${lastName}`,
    bankAccountBsb: '062200',
    bankAccountNumber: '87654321',
    matterPropertyAddress: randomPropertyAddress(),
    matterSettlementDate: defaultSettlementDate(),
  }
}

type PageState = 'loading' | 'invalid' | 'ready' | 'submitted'

export const ClientIntakePage: React.FC<ClientIntakePageProps> = ({ inviteId }) => {
  const [pageState, setPageState] = useState<PageState>('loading')
  const [invalidReason, setInvalidReason] = useState('')
  const [form, setForm] = useState<FormState>(createInitialForm)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedName, setSubmittedName] = useState('')
  const [inviteContext, setInviteContext] = useState<InviteStatus | null>(null)

  useEffect(() => {
    getInviteStatus(inviteId)
      .then((status) => {
        setInviteContext(status)
        setPageState('ready')
      })
      .catch((err) => {
        setInvalidReason(err instanceof Error ? err.message : 'This link is not valid')
        setPageState('invalid')
      })
  }, [inviteId])

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First name and last name are required')
      return
    }

    if (inviteContext?.matterType && !form.matterPropertyAddress.trim()) {
      setError('Property address is required')
      return
    }

    const payload: SubmitClientInvitePayload = {
      firstName: form.firstName,
      lastName: form.lastName,
      emailAddress: form.emailAddress,
      mobileNumber: form.mobileNumber,
      companyName: form.companyName,
      streetAddress: form.streetAddress,
      suburb: form.suburb,
      state: form.state,
      postcode: form.postcode,
      country: form.country,
      source: {
        sourceType: 'bank-account',
        bankAccountName: form.bankAccountName,
        bankAccountBsb: form.bankAccountBsb,
        bankAccountNumber: form.bankAccountNumber,
      },
      ...(inviteContext?.matterType ? {
        matterPropertyAddress: form.matterPropertyAddress,
        matterSettlementDate: form.matterSettlementDate,
      } : {}),
    }

    setSubmitting(true)
    try {
      const result = await submitClientInvite(inviteId, payload)
      setSubmittedName(result.firstName || form.firstName)
      setPageState('submitted')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit your details')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Your Details</h1>
          <p className="text-sm text-gray-600 mt-1">
            {inviteContext?.matterType
              ? `You're completing details for a ${inviteContext.matterType.toLowerCase()}.`
              : 'Please fill this out so your conveyancer can get started.'}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Contact & Settlement Details</CardTitle>
          </CardHeader>
          <CardContent>
            {pageState === 'loading' && <p className="text-gray-600">Loading...</p>}

            {pageState === 'invalid' && <Alert type="error">{invalidReason}</Alert>}

            {pageState === 'submitted' && (
              <Alert type="success">
                Thanks{submittedName ? `, ${submittedName}` : ''}! Your details have been received.
              </Alert>
            )}

            {pageState === 'ready' && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && <Alert type="error">{error}</Alert>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="First Name" required value={form.firstName} onChange={handleChange('firstName')} />
                  <Input label="Last Name" required value={form.lastName} onChange={handleChange('lastName')} />
                  <Input label="Email" type="email" value={form.emailAddress} onChange={handleChange('emailAddress')} />
                  <Input label="Mobile Number" value={form.mobileNumber} onChange={handleChange('mobileNumber')} />
                  <Input label="Company Name (optional)" value={form.companyName} onChange={handleChange('companyName')} />
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Your Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Street Address" value={form.streetAddress} onChange={handleChange('streetAddress')} />
                    <Input label="Suburb" value={form.suburb} onChange={handleChange('suburb')} />
                    <Input label="State" value={form.state} onChange={handleChange('state')} />
                    <Input label="Postcode" value={form.postcode} onChange={handleChange('postcode')} />
                    <Input label="Country" value={form.country} onChange={handleChange('country')} />
                  </div>
                </div>

                {inviteContext?.matterType && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Property Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Input
                          label="Property Address"
                          required
                          value={form.matterPropertyAddress}
                          onChange={handleChange('matterPropertyAddress')}
                        />
                      </div>
                      <Input
                        label="Settlement Date"
                        type="date"
                        value={form.matterSettlementDate}
                        onChange={handleChange('matterSettlementDate')}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Bank Account (for settlement funds)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Account Name" value={form.bankAccountName} onChange={handleChange('bankAccountName')} />
                    <Input label="BSB" value={form.bankAccountBsb} onChange={handleChange('bankAccountBsb')} />
                    <Input label="Account Number" value={form.bankAccountNumber} onChange={handleChange('bankAccountNumber')} />
                  </div>
                </div>

                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Details'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
